/**
 * The route tree. Code-based rather than file-based, so the whole map is one readable page.
 *
 * ADR-0009: a product behind a login stays on Vite plus TanStack Router. This is that.
 */

import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";

import { AppShell } from "./components/app-shell";
import type { NoteCategory } from "./lib/api/types";
import type { AuthContextValue } from "./lib/auth/auth-context";
import {
  DEFAULT_SIGNED_IN_PATH,
  REGISTERED_LANDING_PATH,
  guardProtected,
  guardPublicOnly,
  safeRedirectTarget,
} from "./lib/auth/guard";
import { asCategory } from "./lib/ui/categories";
import { AuthScreen, type AuthMode } from "./screens/auth-screen";
import { EditorScreen } from "./screens/editor-screen";
import { InstallScreen } from "./screens/install-screen";
import { KnowledgeScreen } from "./screens/knowledge-screen";
import { NotFoundScreen } from "./screens/not-found-screen";
import { SettingsScreen } from "./screens/settings-screen";
import { TokensScreen } from "./screens/tokens-screen";

export interface RouterContext {
  auth: AuthContextValue;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundScreen,
});

// --- Public ---------------------------------------------------------------

interface SignInSearch {
  redirect?: string;
}

function authBeforeLoad({
  context,
  search,
}: {
  context: RouterContext;
  search: SignInSearch;
}): void {
  const decision = guardPublicOnly(context.auth.status, search.redirect);
  if (decision.kind === "redirect") throw redirect({ to: decision.to });
}

function AuthRoute({ mode }: { mode: AuthMode }) {
  const navigate = useNavigate();
  return (
    <AuthScreen
      mode={mode}
      onSwitchMode={(next) => {
        void navigate({ to: next === "register" ? "/register" : "/sign-in", search: true });
      }}
    />
  );
}

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    redirect: safeRedirectTarget(typeof search.redirect === "string" ? search.redirect : undefined),
  }),
  beforeLoad: authBeforeLoad,
  component: () => <AuthRoute mode="sign-in" />,
});

/**
 * Register has its own guard, because a new account lands somewhere different.
 *
 * The current design export sends a fresh registration to Install rather than to the library or
 * to a setup wizard — the first thing a new workspace needs is a connected assistant, and Install
 * is the screen that both gives the command and says whether it worked.
 */
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    redirect: safeRedirectTarget(typeof search.redirect === "string" ? search.redirect : undefined),
  }),
  beforeLoad: ({ context, search }: { context: RouterContext; search: SignInSearch }): void => {
    const decision = guardPublicOnly(context.auth.status, search.redirect, REGISTERED_LANDING_PATH);
    if (decision.kind === "redirect") throw redirect({ to: decision.to });
  },
  component: () => <AuthRoute mode="register" />,
});

// --- Everything behind the login -----------------------------------------

/**
 * A pathless layout route. Every screen below it inherits one guard, so protecting a new screen is
 * a matter of adding it here rather than remembering to guard it.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: ({ context, location }) => {
    const decision = guardProtected(context.auth.status, location.href);
    if (decision.kind === "redirect") {
      throw redirect({
        to: decision.to,
        search: decision.redirectParam ? { redirect: decision.redirectParam } : {},
      });
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: DEFAULT_SIGNED_IN_PATH });
  },
  component: () => null,
});

/**
 * The category lives in the URL rather than in component state, so a filtered library is a place
 * somebody can link to, reload and go back from.
 *
 * It is narrowed to the four the service accepts. `?category=skill` — the earlier design's fifth
 * kind, and anything else a URL might carry — resolves to no filter rather than to an empty list
 * that would read as "you have no skills" about a category that does not exist.
 */
interface KnowledgeSearch {
  category?: NoteCategory;
}

const knowledgeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/knowledge",
  validateSearch: (search: Record<string, unknown>): KnowledgeSearch => ({
    category: asCategory(search.category),
  }),
  component: KnowledgeScreen,
});

const editorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/knowledge/$knowledgeId",
  component: EditorScreen,
});

const installRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/install",
  component: InstallScreen,
});

const tokensRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tokens",
  component: TokensScreen,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsScreen,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  registerRoute,
  appRoute.addChildren([
    indexRoute,
    knowledgeRoute,
    editorRoute,
    installRoute,
    tokensRoute,
    settingsRoute,
  ]),
]);

export function buildRouter(auth: AuthContextValue) {
  return createRouter({
    routeTree,
    context: { auth },
    defaultPreload: false,
  });
}

export type AppRouter = ReturnType<typeof buildRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
